import { useCallback, useRef } from "react";

import { ADDRESS_SUBFIELDS } from "./useAddressAutocomplete";

/**
 * Latch-based "has the user touched this address?" gate for
 * {@link useAddressAutocomplete}'s `isDirty` prop. Pre-filled, untouched
 * addresses skip autocomplete entirely until the user edits a subfield.
 *
 * On first render (or first render while `active`), it snapshots the current
 * subfield values; thereafter it reports dirty when the live values diverge
 * from that snapshot, OR when the caller has signalled an edit via
 * `markEdited()` (surfaces call this from their onChange so an autofill that
 * round-trips back to the original value still counts as touched).
 *
 * Surfaces persist their country identifier under different keys (a numeric
 * `countryID`, or `country`); `countryKey` names the extra key to diff beyond
 * the standard `ADDRESS_SUBFIELDS`.
 *
 * @param {Object} src - the surface's current address state.
 * @param {Object} [opts]
 * @param {string} [opts.countryKey="countryID"] - extra key to diff alongside
 *   the standard subfields.
 * @param {boolean} [opts.active=true] - when false, snapshotting is deferred
 *   and the diff is suppressed (e.g. an inline editor not yet in edit mode).
 * @returns {{ isDirty: boolean, markEdited: () => void, reset: () => void }}
 */
export default function useAddressDirty(
  src,
  { countryKey = "countryID", active = true } = {}
) {
  const hasEditedRef = useRef(false);
  const initialRef = useRef(null);

  if (active && initialRef.current === null) {
    initialRef.current = ADDRESS_SUBFIELDS.reduce((acc, fieldName) => {
      acc[fieldName] = src?.[fieldName] ?? "";
      return acc;
    }, {});
    initialRef.current[countryKey] = src?.[countryKey] ?? "";
  }

  const subfieldsChanged =
    active &&
    initialRef.current != null &&
    (ADDRESS_SUBFIELDS.some(
      (fieldName) => (src?.[fieldName] ?? "") !== initialRef.current[fieldName]
    ) ||
      (src?.[countryKey] ?? "") !== initialRef.current[countryKey]);

  const isDirty = hasEditedRef.current || subfieldsChanged;

  const markEdited = useCallback(() => {
    hasEditedRef.current = true;
  }, []);

  const reset = useCallback(() => {
    hasEditedRef.current = false;
    initialRef.current = null;
  }, []);

  return { isDirty, markEdited, reset };
}
